import { createFetch } from "./fetcher";
import { useAuth } from "~/composables/useAuth";
import { ENDPOINT } from "~~/constant/api.constant";
import {
  type IApproveNewVehicleRequestLvl1Response,
  type IApproveVehicleActivationPayload,
  type IAuthLoginResponse,
  type IAuthLogoutResponse,
  type ISlaResponse,
  type IClaimCounterResponse,
  type IClaimBbmRequestResponse,
  type IMaintenanceReason,
  type IMaintenanceSubmitResponse,
  type IMaintenanceVehicleResponse,
  type IMemberResponse,
  type IMonitoringResponse,
  type INIKListResponse,
  type IRejectNewVehicleRequestLvl1payload,
  type IRejectNewVehicleRequestLvl1Response,
  type IVehicleActivationPayload,
  type IVehicleActivationResponse,
  type IVehicleDetailByPlateNumberResponse,
  type IVehicleDetailResponse,
  type IVehicleMaintenanceDetailResponse,
  type IVehicleMaintenanceListResponse,
  type IVehicleMutationApprovePayload,
  type IVehicleMutationPayload,
  type IVehicleMutationRejectPayload,
  type IVehicleMutationResponse,
  type IVehicleOptionsResponse,
  type IVehicleRequestDetailResponse,
  type IVehicleRequestListResponse,
  type IVehiclesResponse,
  type IVehicleSubmitResponse,
  type TActiveMonitoringTab,
  type TMonitoringSortOption,
  type TVehicleRequestList,
  type IListClaimVehicleResponse,
  type IClaimBbmDetailResponse,
  type IBbmClaimSubmissionDetailResponse,
  type IClaimBbmSubmissionPayload,
  type IRejectAndApproveBBMResposne,
  type ICreateBBMSubmissionResponse,
  type IMonitoringTagIResponse,
  type IMonitoringTagIShipmentsResponse,
  type IMonitoringTagIShipmentsParams,
} from "~~/types";
import type {
  ITripDetailResponse,
  ITripResponse,
} from "~~/types/liveTracking/3pl.interface";
import type { IAuthServiceResponse } from "~~/types/liveTracking/auth.interface";
import type {
  ICrew,
  ICrewActiveResponse,
  IDataCrew,
  IDetailTripCrew,
} from "~~/types/liveTracking/crew.interface";
import type { ITrackingReportResponse } from "~~/types/liveTracking/report.interface";
import type {
  IActivitiesResponse,
  ITripDetailData,
} from "~~/types/liveTracking/trip.interface";

const api = createFetch();
const { getAccessToken } = useAuth();

const getHeaders = (opts?: {
  withAuth?: boolean;
  isForm?: boolean;
  strCode?: string;
  customHeaders?: Record<string, string>;
}) => {
  const headers: Record<string, string> = {
    appVersion: "3",
    accept: "application/json",
  };

  if (opts?.isForm) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
  }

  if (opts?.withAuth) {
    const token = getAccessToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  // Merge custom headers
  if (opts?.customHeaders) {
    Object.assign(headers, opts.customHeaders);
  }

  return headers;
};

const toFormBody = (body: Record<string, any>) => {
  const params = new URLSearchParams();

  Object.entries(body).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });

  return params;
};

export const apiService = {
  auth: {
    login: (body: { nik: string; pin: string }) =>
      api.post<IAuthLoginResponse>(ENDPOINT.AUTH.LOGIN, {
        body: toFormBody(body),
        headers: getHeaders({ isForm: true }),
      }),

    refresh: (rt: string, appVersion: number) =>
      api.post<IAuthLoginResponse>(ENDPOINT.AUTH.REFRESH, {
        params: { rt, appVersion },
      }),

    logout: (body: { nik: string }) =>
      api.post<IAuthLogoutResponse>(ENDPOINT.AUTH.LOGOUT, {
        body: toFormBody(body),
        headers: getHeaders({ withAuth: true, isForm: true }),
      }),

    info: () =>
      api.get<IMemberResponse>(ENDPOINT.AUTH.INFO, {
        headers: getHeaders({ withAuth: true }),
      }),
  },
  monitoring: {
    list: (type: TActiveMonitoringTab, sort?: TMonitoringSortOption) =>
      api.get<IMonitoringResponse>(ENDPOINT.MONITORING.LIST, {
        params: { type, sort },
        headers: getHeaders({ withAuth: true }),
      }),
    sla: (sort?: TMonitoringSortOption) =>
      api.get<ISlaResponse>(ENDPOINT.MONITORING.SLA, {
        params: { sort },
        headers: getHeaders({ withAuth: true }),
      }),
    tagList: () =>
      api.get<IMonitoringTagIResponse>(ENDPOINT.MONITORING.TAG_I, {
        headers: getHeaders({ withAuth: true }),
      }),
    tagShipments: (params?: IMonitoringTagIShipmentsParams) =>
      api.get<IMonitoringTagIShipmentsResponse>(
        ENDPOINT.MONITORING.TAG_I_SHIPMENTS,
        {
          params,
          headers: getHeaders({ withAuth: true })
        },
      ), 
  },
  tracking: {
    auth: (params?: Record<string, any>) => {
      return api.get<IAuthServiceResponse>(ENDPOINT.TRACKING.AUTH, {
        headers: getHeaders({ withAuth: true }),
        params: params,
      });
    },
    trip: (params?: Record<string, any>) =>
      api.get<any>(ENDPOINT.TRACKING.TRIP, {
        headers: getHeaders({ withAuth: true }),
        params: params,
      }),

    detailTrip: (tripId: string) =>
      api.get<ITripDetailData>(`${ENDPOINT.TRACKING.TRIP}/${tripId}`, {
        headers: getHeaders({ withAuth: true }),
      }),

    crew: (params?: Record<string, any>) =>
      api.get<ICrew>(ENDPOINT.TRACKING.CREW, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),

    crewNik: (nik: string) =>
      api.get<IDataCrew>(`${ENDPOINT.TRACKING.CREW}/${nik}`, {
        headers: getHeaders({ withAuth: true }),
      }),

    crewDetail: (nik: string) =>
      api.get<IDataCrew>(`${ENDPOINT.TRACKING.CREW}/${nik}`, {
        headers: getHeaders({ withAuth: true }),
      }),

    detailCrewTrip: (nik: string) =>
      api.get<IDetailTripCrew>(`${ENDPOINT.TRACKING.CREW}/${nik}/trips`, {
        headers: getHeaders({ withAuth: true }),
      }),

    report: (params?: Record<string, any>) =>
      api.get<ITrackingReportResponse>(`${ENDPOINT.TRACKING.REPORT}`, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),

    activeCrew: (params?: Record<string, any>) =>
      api.get<ICrewActiveResponse>(`${ENDPOINT.TRACKING.ACTIVECREW}`, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),

    activity: (tripId: string) =>
      api.get<IActivitiesResponse>(
        `${ENDPOINT.TRACKING.TRIP}/${tripId}/activity`,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),
    trip3pl: (params?: Record<string, any>) =>
      api.get<ITripResponse>(ENDPOINT.TRACKING.THREEPL, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),
    detail3pl: (tripId: string) =>
      api.get<ITripDetailResponse>(`${ENDPOINT.TRACKING.THREEPL}/${tripId}`, {
        headers: getHeaders({ withAuth: true }),
      }),
    report3pl: (params?: Record<string, any>) =>
      api.get<any>(ENDPOINT.TRACKING.REPORT3PL, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),
  },
  role: {
    employeeList: <T>(store: string) =>
      api.get<T>(`${ENDPOINT.ROLE.EMPLOYEE_LIST}?query=${store}`, {
        headers: getHeaders({ withAuth: true }),
      }),
    roleList: <T>(store: string) =>
      api.get<T>(`${ENDPOINT.ROLE.HISTORY}?query=${store}`, {
        headers: getHeaders({ withAuth: true }),
      }),
    changeRole: <T>(employeeNik: string, employeeName: string) =>
      api.post<T>(ENDPOINT.ROLE.UPDATE, {
        body: toFormBody({ employeeNik, employeeName }),
        headers: getHeaders({ withAuth: true, isForm: true }),
      }),
    resetRole: <T>(employeeNik: string, employeeName: string) =>
      api.post<T>(ENDPOINT.ROLE.RESET, {
        body: toFormBody({ employeeNik, employeeName }),
        headers: getHeaders({ withAuth: true, isForm: true }),
      }),
  },
  claimOperationalSapa: {
    claimCounter: () =>
      api.get<IClaimCounterResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_COUNTER,
        {
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleList: () =>
      api.get<IVehiclesResponse>(ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_LIST, {
        headers: getHeaders({
          withAuth: true,
        }),
      }),

    vehicleActivationList: () =>
      api.get<IVehiclesResponse>(ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_LIST, {
        params: {
          activation: true,
        },
        headers: getHeaders({
          withAuth: true,
        }),
      }),

    vehicleRequestList: (
      submissionType: TVehicleRequestList,
      startDate?: string,
      endDate?: string,
    ) =>
      api.get<IVehicleRequestListResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_REQUEST_LIST,
        {
          params: {
            submissionType,
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
          },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleRequestDetail: (referenceNumber: string, storeCode: string) =>
      api.get<IVehicleRequestDetailResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_REQUEST_LIST}/${referenceNumber}`,
        {
          params: { storeCode },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleRequestApproveLvl1: (referenceNumber: string) =>
      api.post<IApproveNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_APPROVAL}/${referenceNumber}/approve-lv1`,
        {
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleRequestRejectLvl1: (payload: IRejectNewVehicleRequestLvl1payload) =>
      api.post<IRejectNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_REJECT_LVL1}`,
        {
          body: payload,
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleActivationApproveLvl1: (
      referenceNumber: string,
      payload: IApproveVehicleActivationPayload,
    ) =>
      api.post<IRejectNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_ACTIVATION_APPROVAL}/${referenceNumber}/approve-lv1`,
        {
          body: payload,
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleActivationRejectLvl1: (
      referenceNumber: string,
      rejectReason: string,
    ) =>
      api.post<IRejectNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_ACTIVATION_APPROVAL}/${referenceNumber}/reject-lv1`,
        {
          body: { rejectReason },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleMutationApproveLvl1: (
      referenceNumber: string,
      payload: IVehicleMutationApprovePayload,
    ) =>
      api.post<IRejectNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MUTATION_APPROVAL}/${referenceNumber}/approve-lv1`,
        {
          body: payload,
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleMutationRejectLvl1: (
      referenceNumber: string,
      payload: IVehicleMutationRejectPayload,
    ) =>
      api.post<IRejectNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MUTATION_APPROVAL}/${referenceNumber}/reject-lv1`,
        {
          body: payload,
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    vehicleDetailByPlateNumber: (plateNumber: string) =>
      api.get<IVehicleDetailByPlateNumberResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_DETAIL_BY_PLATE_NUMBER}/${plateNumber}`,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),

    vehicleDetail: (vehiclePlateNumber: string) =>
      api.get<IVehicleDetailResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_DETAIL}/${vehiclePlateNumber}/details`,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),

    vehicleOptions: () =>
      api.get<IVehicleOptionsResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_OPTIONS,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),

    vehicleSubmit: (payload: FormData) =>
      api.post<IVehicleSubmitResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_SUBMIT,
        {
          body: payload,
          headers: getHeaders({ withAuth: true }),
        },
      ),

    vehicleMutationSubmit: (payload: IVehicleMutationPayload) =>
      api.post<IVehicleMutationResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MUTATION_SUBMIT,
        {
          body: payload,
          headers: getHeaders({ withAuth: true }),
        },
      ),

    vehicleActivationSubmit: (payload: IVehicleActivationPayload) =>
      api.post<IVehicleActivationResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_ACTIVATION_SUBMIT,
        {
          body: payload,
          headers: getHeaders({ withAuth: true }),
        },
      ),

    getNIKList: (storeId: string, selectedNik?: string) =>
      api.get<INIKListResponse>(ENDPOINT.CLAIM_OPERATIONAL_SAPA.NIK_LIST, {
        params: {
          storeId,
          selectedNik,
        },
        headers: getHeaders({ withAuth: true }),
      }),

    // claim maintenance
    maintenanceList: (startDate?: string, endDate?: string) =>
      api.get<IVehicleMaintenanceListResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE,
        {
          params: {
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
          },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    maintenanceDetail: (referenceNumber: string, storeCode: string) =>
      api.get<IVehicleMaintenanceDetailResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE}/${referenceNumber}`,
        {
          params: { storeCode },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    maintenanceApproveLvl1: (referenceNumber: string) =>
      api.post<IApproveNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE}/${referenceNumber}/approve-lv1`,
        {
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    maintenanceRejectLvl1: (referenceNumber: string, rejectReason: string) =>
      api.post<IApproveNewVehicleRequestLvl1Response>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE}/${referenceNumber}/reject-lv1`,
        {
          body: { rejectReason },
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    maintenanceVehicleList: () =>
      api.get<IMaintenanceVehicleResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE_VEHICLES,
        {
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    maintenanceReasonList: () =>
      api.get<IMaintenanceReason[]>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE_REASON,
        {
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    manintenanceSubmit: (payload: FormData) =>
      api.post<IMaintenanceSubmitResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.VEHICLE_MAINTENANCE_SUBMIT,
        {
          body: payload,
          headers: getHeaders({
            withAuth: true,
          }),
        },
      ),

    getListClaimBbm: (startDate?: string, endDate?: string) =>
      api.get<IClaimBbmRequestResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_LIST,
        {
          headers: getHeaders({ withAuth: true }),
          params: {
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
          },
        },
      ),

    getClaimVehicleOptions: (assetType: string, selectedVehicle?: string) =>
      api.get<IListClaimVehicleResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_VEHICLE_OPTIONS,
        {
          headers: getHeaders({ withAuth: true }),
          params: { assetType, selectedVehicle },
        },
      ),

    getClaimBbmDetail: (storeCode: string, referenceNumber: string) =>
      api.get<IClaimBbmDetailResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_BBM_DETAIL}/${storeCode}/${referenceNumber}`,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),

    claimBbmSubmissionApproveLvl1: (
      referenceNumber: string,
      storeCode: string,
    ) =>
      api.post<IRejectAndApproveBBMResposne>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_BBM_SUBMISSION_APPROVE_LVL1}/${referenceNumber}/approve-lv1`,
        {
          body: { storeCode },
          headers: getHeaders({ withAuth: true }),
        },
      ),

    getDetailBbmSubmission: (plateNumber: string) =>
      api.get<IBbmClaimSubmissionDetailResponse>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_DETAIL_BBM_SUBMISSION}/${plateNumber}`,
        {
          headers: getHeaders({ withAuth: true }),
        },
      ),

    createPrivateBbmSubmission: (payload: IClaimBbmSubmissionPayload) =>
      api.post<ICreateBBMSubmissionResponse>(
        ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_CREATE_PRIVATE_BBM_SUBMISSION,
        {
          body: toFormBody(payload),
          headers: {
            ...getHeaders({ withAuth: true }),
          },
        },
      ),

    submitRejectSubmission: (
      referenceNumber: string,
      rejectReason: string,
      storeCode: string,
    ) =>
      api.post<IRejectAndApproveBBMResposne>(
        `${ENDPOINT.CLAIM_OPERATIONAL_SAPA.CLAIM_REJECT_SUBMISSION}/${referenceNumber}/reject-lv1`,
        {
          body: { rejectReason, storeCode },
          headers: {
            ...getHeaders({ withAuth: true }),
          },
        },
      ),
  },
  refund: {
    refundList: (params?: Record<string, any>) =>
      api.get<any>(ENDPOINT.REFUND.REFUND_LIST, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),
    exportRefund: (params?: Record<string, any>) =>
      api.get<any>(ENDPOINT.REFUND.EXPORT_REFUND_DATA, {
        headers: getHeaders({ withAuth: true }),
        params,
      }),
  },
};
